import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import BreadCrumb from "../../../../../Components/Common/BreadCrumb";
import HelpArticleLink from "../../../../../Components/Common/HelpArticleLink";
import { HELP_ARTICLE_EXPENSES } from "../../../../../helpers/helpCenter";
import { entityFormKey } from "../../../../../helpers/entityEditForm";
import {
  formatCentsToMoneyInput,
  formatMoneyFromCents,
  parseMoneyInputToCents,
} from "../../../../../helpers/money";
import {
  createExpense,
  CreateExpensePayload,
  deleteExpense,
  EXPENSE_CATEGORIES,
  EXPENSE_COST_TYPES,
  Expense,
  ExpenseCategory,
  ExpenseCostType,
  getExpense,
  getExpenses,
  updateExpense,
} from "../../../../../services/expenseService";
import { getUnits, Unit } from "../../../../../services/unitService";
import { PaginationMeta } from "../../../../../types/api";

const PER_PAGE = 15;
const SEARCH_DEBOUNCE_MS = 400;
const UNITS_PAGE_SIZE = 100;

interface ExpenseFormValues {
  unit_id: string;
  cost_type: ExpenseCostType;
  category: ExpenseCategory;
  description: string;
  amount_display: string;
  incurred_at_local: string;
  notes: string;
}

const toDatetimeLocalValue = (iso?: string): string => {
  if (!iso) {
    const now = new Date();
    const pad = (value: number) => String(value).padStart(2, "0");

    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  }

  const date = new Date(iso);
  const pad = (value: number) => String(value).padStart(2, "0");

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const datetimeLocalToUtcIso = (value: string): string => new Date(value).toISOString();

const emptyFormValues = (): ExpenseFormValues => ({
  unit_id: "",
  cost_type: "fixed",
  category: "other",
  description: "",
  amount_display: "",
  incurred_at_local: toDatetimeLocalValue(),
  notes: "",
});

const mapExpenseToFormValues = (expense: Expense): ExpenseFormValues => ({
  unit_id: String(expense.unit_id),
  cost_type: expense.cost_type,
  category: expense.category,
  description: expense.description,
  amount_display: formatCentsToMoneyInput(expense.amount_cents),
  incurred_at_local: toDatetimeLocalValue(expense.incurred_at),
  notes: expense.notes ?? "",
});

const buildExpensePayload = (values: ExpenseFormValues): CreateExpensePayload => ({
  unit_id: Number(values.unit_id),
  cost_type: values.cost_type,
  category: values.category,
  description: values.description.trim(),
  amount_cents: parseMoneyInputToCents(values.amount_display),
  incurred_at: datetimeLocalToUtcIso(values.incurred_at_local),
  notes: values.notes.trim() || null,
});

const ExpensesList = () => {
  const { t, i18n } = useTranslation();

  document.title = t("studio.financial.expenses.pageTitle");

  const formatMoney = useCallback(
    (cents: number) => formatMoneyFromCents(cents, i18n.language),
    [i18n.language]
  );

  const formatDateTime = useCallback(
    (value: string) =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: "short",
        timeStyle: "short",
      }).format(new Date(value)),
    [i18n.language]
  );

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [costTypeFilter, setCostTypeFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [fromFilter, setFromFilter] = useState("");
  const [toFilter, setToFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [offcanvasOpen, setOffcanvasOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Expense | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);

  const notAvailable = t("studio.financial.expenses.common.notAvailable");

  const validationSchema = useMemo(
    () =>
      Yup.object({
        unit_id: Yup.string().required(t("studio.financial.expenses.validation.unitRequired")),
        cost_type: Yup.string()
          .oneOf(EXPENSE_COST_TYPES)
          .required(t("studio.financial.expenses.validation.costTypeRequired")),
        category: Yup.string()
          .oneOf(EXPENSE_CATEGORIES)
          .required(t("studio.financial.expenses.validation.categoryRequired")),
        description: Yup.string()
          .trim()
          .required(t("studio.financial.expenses.validation.descriptionRequired")),
        amount_display: Yup.string()
          .required(t("studio.financial.expenses.validation.amountRequired"))
          .test("amount-valid", t("studio.financial.expenses.validation.amountInvalid"), (value) => {
            return parseMoneyInputToCents(value || "") > 0;
          }),
        incurred_at_local: Yup.string().required(
          t("studio.financial.expenses.validation.incurredAtRequired")
        ),
        notes: Yup.string().max(2000, t("studio.financial.expenses.validation.notesMax")),
      }),
    [t]
  );

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, unitFilter, costTypeFilter, categoryFilter, fromFilter, toFilter]);

  const loadUnits = useCallback(async () => {
    try {
      const response = await getUnits({ per_page: UNITS_PAGE_SIZE });
      setUnits(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.financial.expenses.toast.loadError"));
    }
  }, [t]);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getExpenses({
        page,
        per_page: PER_PAGE,
        ...(unitFilter ? { unit_id: Number(unitFilter) } : {}),
        ...(costTypeFilter ? { cost_type: costTypeFilter as ExpenseCostType } : {}),
        ...(categoryFilter ? { category: categoryFilter as ExpenseCategory } : {}),
        ...(fromFilter ? { from: fromFilter } : {}),
        ...(toFilter ? { to: toFilter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      setExpenses(response.data);
      setMeta(response.meta);
    } catch (error: any) {
      toast.error(error?.data || t("studio.financial.expenses.toast.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    debouncedSearch,
    unitFilter,
    costTypeFilter,
    categoryFilter,
    fromFilter,
    toFilter,
    t,
  ]);

  useEffect(() => {
    loadUnits();
  }, [loadUnits]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const formInitialValues = useMemo(
    () =>
      editingExpense
        ? mapExpenseToFormValues(editingExpense)
        : { ...emptyFormValues(), unit_id: unitFilter || "" },
    [editingExpense, unitFilter]
  );

  const formik = useFormik<ExpenseFormValues>({
    enableReinitialize: true,
    initialValues: formInitialValues,
    validationSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      const payload = buildExpensePayload(values);

      try {
        if (editingExpense) {
          const response = await updateExpense(editingExpense.id, payload);
          toast.success(response.message);
        } else {
          const response = await createExpense(payload);
          toast.success(response.message);
        }
        closeOffcanvas();
        if (!editingExpense && page !== 1) {
          setPage(1);
        } else {
          await fetchExpenses();
        }
      } catch (error: any) {
        toast.error(error?.data || t("studio.financial.expenses.toast.saveError"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const closeOffcanvas = () => {
    setOffcanvasOpen(false);
    setEditingExpense(null);
    setFormLoading(false);
  };

  const openCreate = () => {
    setEditingExpense(null);
    setFormLoading(false);
    setOffcanvasOpen(true);
  };

  const openEdit = async (expense: Expense) => {
    setEditingExpense(null);
    setFormLoading(true);
    setOffcanvasOpen(true);

    try {
      const response = await getExpense(expense.id);
      setEditingExpense(response.data);
    } catch (error: any) {
      toast.error(error?.data || t("studio.financial.expenses.toast.loadError"));
      closeOffcanvas();
    } finally {
      setFormLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }

    setSubmitting(true);
    try {
      const response = await deleteExpense(deleteTarget.id);
      toast.success(response.message);
      setDeleteModal(false);
      setDeleteTarget(null);
      const isLastOnPage = expenses.length === 1 && page > 1;
      if (isLastOnPage) {
        setPage(page - 1);
      } else {
        await fetchExpenses();
      }
    } catch (error: any) {
      toast.error(error?.data || t("studio.financial.expenses.toast.deleteError"));
    } finally {
      setSubmitting(false);
    }
  };

  const resolveUnitName = (expense: Expense): string =>
    expense.unit?.name ?? units.find((unit) => unit.id === expense.unit_id)?.name ?? notAvailable;

  const resolveCostTypeLabel = (costType: ExpenseCostType): string =>
    t(`studio.financial.expenses.costType.${costType}`);

  const resolveCategoryLabel = (category: ExpenseCategory): string =>
    t(`studio.financial.expenses.category.${category}`);

  return (
    <div className="page-content">
      <ToastContainer closeButton={false} limit={1} />
      <Container fluid>
        <BreadCrumb
          title={t("studio.financial.expenses.breadcrumb.title")}
          pageTitle={t("studio.financial.expenses.breadcrumb.pageTitle")}
        />
        <div className="d-flex justify-content-end mb-3">
          <HelpArticleLink articlePath={HELP_ARTICLE_EXPENSES} />
        </div>
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex align-items-center flex-wrap gap-2">
                <h4 className="card-title mb-0 flex-grow-1">
                  {t("studio.financial.expenses.card.title")}
                </h4>
                <Button color="primary" onClick={openCreate}>
                  <i className="ri-add-line align-bottom me-1" />
                  {t("studio.financial.expenses.actions.new")}
                </Button>
              </CardHeader>
              <CardBody>
                <Row className="g-2 mb-3">
                  <Col md={3}>
                    <Input
                      type="select"
                      value={unitFilter}
                      onChange={(event) => setUnitFilter(event.target.value)}
                      aria-label={t("studio.financial.expenses.filter.unit")}
                    >
                      <option value="">{t("studio.financial.expenses.filter.allUnits")}</option>
                      {units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Input
                      type="select"
                      value={costTypeFilter}
                      onChange={(event) => setCostTypeFilter(event.target.value)}
                      aria-label={t("studio.financial.expenses.filter.costType")}
                    >
                      <option value="">{t("studio.financial.expenses.filter.allCostTypes")}</option>
                      {EXPENSE_COST_TYPES.map((costType) => (
                        <option key={costType} value={costType}>
                          {resolveCostTypeLabel(costType)}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Input
                      type="select"
                      value={categoryFilter}
                      onChange={(event) => setCategoryFilter(event.target.value)}
                      aria-label={t("studio.financial.expenses.filter.category")}
                    >
                      <option value="">{t("studio.financial.expenses.filter.allCategories")}</option>
                      {EXPENSE_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                          {resolveCategoryLabel(category)}
                        </option>
                      ))}
                    </Input>
                  </Col>
                  <Col md={2}>
                    <Input
                      type="date"
                      value={fromFilter}
                      onChange={(event) => setFromFilter(event.target.value)}
                      aria-label={t("studio.financial.expenses.filter.from")}
                    />
                  </Col>
                  <Col md={2}>
                    <Input
                      type="date"
                      value={toFilter}
                      onChange={(event) => setToFilter(event.target.value)}
                      aria-label={t("studio.financial.expenses.filter.to")}
                    />
                  </Col>
                  <Col md={12}>
                    <Input
                      type="search"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder={t("studio.financial.expenses.search.placeholder")}
                    />
                  </Col>
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary">{t("studio.financial.expenses.loading")}</Spinner>
                  </div>
                ) : expenses.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted mb-3">
                      {debouncedSearch
                        ? t("studio.financial.expenses.empty.noResults", { term: debouncedSearch })
                        : t("studio.financial.expenses.empty.noData")}
                    </p>
                    <Button color="primary" onClick={openCreate}>
                      {t("studio.financial.expenses.actions.createFirst")}
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>{t("studio.financial.expenses.table.description")}</th>
                            <th>{t("studio.financial.expenses.table.unit")}</th>
                            <th>{t("studio.financial.expenses.table.category")}</th>
                            <th>{t("studio.financial.expenses.table.costType")}</th>
                            <th>{t("studio.financial.expenses.table.amount")}</th>
                            <th>{t("studio.financial.expenses.table.incurredAt")}</th>
                            <th className="text-end">{t("studio.financial.expenses.table.actions")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {expenses.map((expense) => (
                            <tr key={expense.id}>
                              <td>{expense.description}</td>
                              <td>{resolveUnitName(expense)}</td>
                              <td>
                                <Badge color="soft-secondary">
                                  {resolveCategoryLabel(expense.category)}
                                </Badge>
                              </td>
                              <td>
                                <Badge
                                  color={expense.cost_type === "fixed" ? "primary" : "info"}
                                >
                                  {resolveCostTypeLabel(expense.cost_type)}
                                </Badge>
                              </td>
                              <td>{formatMoney(expense.amount_cents)}</td>
                              <td>{formatDateTime(expense.incurred_at)}</td>
                              <td className="text-end">
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  className="me-1"
                                  title={t("studio.financial.expenses.actions.edit")}
                                  onClick={() => openEdit(expense)}
                                >
                                  <i className="ri-pencil-line" />
                                </Button>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  title={t("studio.financial.expenses.actions.delete")}
                                  onClick={() => {
                                    setDeleteTarget(expense);
                                    setDeleteModal(true);
                                  }}
                                >
                                  <i className="ri-delete-bin-line" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {meta && meta.last_page > 1 && (
                      <Row className="align-items-center mt-3">
                        <Col sm={6}>
                          <div className="text-muted">
                            {t("studio.financial.expenses.pagination.showing", {
                              from: meta.from ?? 0,
                              to: meta.to ?? 0,
                              total: meta.total,
                            })}
                          </div>
                        </Col>
                        <Col sm={6}>
                          <Pagination className="pagination-separated justify-content-sm-end mb-0">
                            <PaginationItem disabled={page <= 1}>
                              <PaginationLink
                                previous
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page > 1) {
                                    setPage(page - 1);
                                  }
                                }}
                              >
                                {t("studio.financial.expenses.pagination.previous")}
                              </PaginationLink>
                            </PaginationItem>
                            <PaginationItem disabled={page >= meta.last_page}>
                              <PaginationLink
                                next
                                href="#"
                                onClick={(event) => {
                                  event.preventDefault();
                                  if (page < meta.last_page) {
                                    setPage(page + 1);
                                  }
                                }}
                              >
                                {t("studio.financial.expenses.pagination.next")}
                              </PaginationLink>
                            </PaginationItem>
                          </Pagination>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Offcanvas isOpen={offcanvasOpen} toggle={closeOffcanvas} direction="end">
        <OffcanvasHeader toggle={closeOffcanvas}>
          {editingExpense
            ? t("studio.financial.expenses.form.editTitle")
            : t("studio.financial.expenses.form.createTitle")}
        </OffcanvasHeader>
        <OffcanvasBody>
          {formLoading ? (
            <div className="text-center py-5">
              <Spinner color="primary">{t("clients.loading")}</Spinner>
            </div>
          ) : (
          <Form key={entityFormKey(editingExpense?.id)} onSubmit={formik.handleSubmit}>
            <div className="mb-3">
              <Label htmlFor="expense-unit">{t("studio.financial.expenses.fields.unit.label")}</Label>
              <Input
                id="expense-unit"
                name="unit_id"
                type="select"
                value={formik.values.unit_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.unit_id && formik.errors.unit_id)}
              >
                <option value="">{t("studio.financial.expenses.fields.unit.placeholder")}</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.name}
                  </option>
                ))}
              </Input>
              <FormFeedback>{formik.errors.unit_id}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="expense-cost-type">
                {t("studio.financial.expenses.fields.costType.label")}
              </Label>
              <Input
                id="expense-cost-type"
                name="cost_type"
                type="select"
                value={formik.values.cost_type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {EXPENSE_COST_TYPES.map((costType) => (
                  <option key={costType} value={costType}>
                    {resolveCostTypeLabel(costType)}
                  </option>
                ))}
              </Input>
            </div>
            <div className="mb-3">
              <Label htmlFor="expense-category">
                {t("studio.financial.expenses.fields.category.label")}
              </Label>
              <Input
                id="expense-category"
                name="category"
                type="select"
                value={formik.values.category}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {EXPENSE_CATEGORIES.map((category) => (
                  <option key={category} value={category}>
                    {resolveCategoryLabel(category)}
                  </option>
                ))}
              </Input>
            </div>
            <div className="mb-3">
              <Label htmlFor="expense-description">
                {t("studio.financial.expenses.fields.description.label")}
              </Label>
              <Input
                id="expense-description"
                name="description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.financial.expenses.fields.description.placeholder")}
                invalid={!!(formik.touched.description && formik.errors.description)}
              />
              <FormFeedback>{formik.errors.description}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="expense-amount">
                {t("studio.financial.expenses.fields.amount.label")}
              </Label>
              <Input
                id="expense-amount"
                name="amount_display"
                value={formik.values.amount_display}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.financial.expenses.fields.amount.placeholder")}
                invalid={!!(formik.touched.amount_display && formik.errors.amount_display)}
              />
              <FormFeedback>{formik.errors.amount_display}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="expense-incurred-at">
                {t("studio.financial.expenses.fields.incurredAt.label")}
              </Label>
              <Input
                id="expense-incurred-at"
                name="incurred_at_local"
                type="datetime-local"
                value={formik.values.incurred_at_local}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={
                  !!(formik.touched.incurred_at_local && formik.errors.incurred_at_local)
                }
              />
              <FormFeedback>{formik.errors.incurred_at_local}</FormFeedback>
            </div>
            <div className="mb-3">
              <Label htmlFor="expense-notes">{t("studio.financial.expenses.fields.notes.label")}</Label>
              <Input
                id="expense-notes"
                name="notes"
                type="textarea"
                rows={3}
                value={formik.values.notes}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder={t("studio.financial.expenses.fields.notes.placeholder")}
                invalid={!!(formik.touched.notes && formik.errors.notes)}
              />
              <FormFeedback>{formik.errors.notes}</FormFeedback>
            </div>
            <div className="d-flex gap-2 justify-content-end">
              <Button color="light" onClick={closeOffcanvas}>
                {t("studio.financial.expenses.actions.cancel")}
              </Button>
              <Button color="primary" type="submit" disabled={submitting}>
                {submitting
                  ? t("studio.financial.expenses.loading")
                  : t("studio.financial.expenses.actions.save")}
              </Button>
            </div>
          </Form>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)} centered>
        <ModalBody className="py-3 px-5">
          <div className="mt-2 text-center">
            <h4>{t("studio.financial.expenses.delete.title")}</h4>
            <p className="text-muted mx-4 mb-0">
              {deleteTarget
                ? t("studio.financial.expenses.delete.description", {
                    name: deleteTarget.description,
                  })
                : t("studio.financial.expenses.delete.fallback")}
            </p>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4">
            <Button color="light" onClick={() => setDeleteModal(false)}>
              {t("studio.financial.expenses.actions.cancel")}
            </Button>
            <Button color="danger" onClick={confirmDelete} disabled={submitting}>
              {t("studio.financial.expenses.actions.confirmDelete")}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
};

export default ExpensesList;
